import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/currencyUtil';
import { POS_PAYMENT_NOTE } from '../constants/posConst';
import '../css/Pos.css';

/**
 * One order, laid out as a printed receipt.
 *
 * Shared by three places — the confirmation before charging, the receipt
 * shown once an order is recorded, and reading one back from Sales — so a
 * receipt looks identical whichever way you arrive at it. Staff comparing the
 * screen against a paper slip should not have to translate between layouts.
 *
 * CASH and CHANGE only appear when cash was actually entered. Printing
 * "CHANGE ₱0" on a card or GCash sale would be stating something untrue about
 * the transaction.
 *
 * @param {object} props
 * @param {{items: object[], total: number, createdAt?: string, cashier?: string, cashReceived?: number, change?: number}} props.order
 * @param {string} props.courtName
 * @param {string} [props.reference] - the order's id, once it has one
 * @param {boolean} [props.showPaymentNote] - the "paid first" warning; only
 *   before charging, where it is an instruction rather than history
 * @returns {JSX.Element}
 */
function PosReceipt({ order, courtName, reference = null, showPaymentNote = false }) {
  const { config } = useConfig();
  /** One place, so every figure on the receipt matches. */
  // Defined per component rather than at module scope: the symbol comes from
  // Config -> System, so it needs `config` in hand. Call sites read the same
  // as they always did.
  const peso = (value) => formatMoney(value, config);

  const items = order?.items || [];
  const when = order?.createdAt ? new Date(order.createdAt) : new Date();
  const hasCash = Number.isFinite(Number(order?.cashReceived)) && Number(order?.cashReceived) > 0;

  return (
    <div className="pos-receipt">
      <div className="pos-receipt-rule" aria-hidden="true" />

      <h3 className="pos-receipt-title">RECEIPT</h3>
      <p className="pos-receipt-shop">{courtName || 'Point of Sale'}</p>

      <div className="pos-receipt-rule" aria-hidden="true" />

      <ul className="pos-receipt-lines">
        {items.map((item, index) => (
          // Index in the key: one order can legitimately hold the same product
          // twice, so productId alone would collide.
          <li key={`${item.productId || item.name}-${index}`}>
            <span className="pos-receipt-qty">{item.quantity}x</span>
            <span className="pos-receipt-name">{item.name}</span>
            <span className="pos-receipt-amount">{peso(item.lineTotal)}</span>
          </li>
        ))}
      </ul>

      <div className="pos-receipt-rule" aria-hidden="true" />

      <div className="pos-receipt-line pos-receipt-grand">
        <span>TOTAL AMOUNT</span>
        <span>{peso(order?.total)}</span>
      </div>

      {hasCash && (
        <>
          <div className="pos-receipt-rule" aria-hidden="true" />
          <div className="pos-receipt-line">
            <span>CASH</span>
            <span>{peso(order.cashReceived)}</span>
          </div>
          <div className="pos-receipt-line">
            <span>CHANGE</span>
            <span>{peso(order.change)}</span>
          </div>
        </>
      )}

      <p className="pos-receipt-thanks">THANK YOU</p>

      <div className="pos-receipt-rule" aria-hidden="true" />

      <p className="pos-receipt-foot">
        {when.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
        {order?.cashier ? ` · ${order.cashier}` : ''}
      </p>
      {reference && <p className="pos-receipt-foot pos-receipt-ref">Ref {reference}</p>}

      {showPaymentNote && <p className="pos-receipt-note">{POS_PAYMENT_NOTE}</p>}
    </div>
  );
}

export default PosReceipt;
